// ---------------------------------------------------------------------------
// Utility domain types
// Ported from trusstor_dashboard/shared/interfaces/
// ---------------------------------------------------------------------------

import { z } from 'zod';
import { IMinimizedProfession, minimizedProfessionSchema, tradeSchema } from './profession.js';
import { IBaseStaticTag, IBaseMergeTag } from './tag.js';

// ---------------------------------------------------------------------------
// UtilityTypes
// (from trusstor_dashboard/shared/constants/utilityTypes.enum.ts)
// ---------------------------------------------------------------------------

export enum UtilityTypes {
	hoist = 'hoist',
	scissorsLift = 'scissorsLift',
}

// ---------------------------------------------------------------------------
// utilitySchema / IUtility<T>
// (from trusstor_dashboard/shared/interfaces/IUtility.ts)
// _id is optional (inherited from tradeSchema where _id: z.string().optional())
// ---------------------------------------------------------------------------

export const utilitySchema = tradeSchema
	.extend({
		type: z.enum(UtilityTypes),
		projectId: z.string(),
		profession: minimizedProfessionSchema.optional(),
		name: z.string().optional(),
	})
	.passthrough();

type IUtilityBase = Omit<z.infer<typeof utilitySchema>, 'profession' | 'name'>;

export type IHoistUtility = IUtilityBase;

export type IScissorsLiftUtility<T extends IMinimizedProfession = IMinimizedProfession> = IUtilityBase & {
	profession: T;
	name: string;
};

export type IUtility<T extends IMinimizedProfession = IMinimizedProfession> = IHoistUtility | IScissorsLiftUtility<T>;

// ---------------------------------------------------------------------------
// IUtilityStaticTag<T>
// (from trusstor_dashboard/shared/interfaces/IUtilityStaticTag.ts)
// ---------------------------------------------------------------------------

export interface IUtilityStaticTag<T extends IMinimizedProfession = IMinimizedProfession> extends IBaseStaticTag {
	linkedUtility: IUtility<T>;
}

// ---------------------------------------------------------------------------
// IUtilityMergedTag<T>
// (from trusstor_dashboard/shared/interfaces/IUtilityMergedTag.ts)
// ---------------------------------------------------------------------------

export interface IUtilityMergedTag<T extends IMinimizedProfession = IMinimizedProfession>
	extends IUtilityStaticTag<T>,
		IBaseMergeTag {}
